"use client";

import { localizeHref } from "@/lib/localize-href";
import { useState, useEffect, useRef, useCallback } from "react";
import { INTERNATIONAL_POPS } from "@/lib/network-data";
import { trackEvent } from "@/lib/analytics";
import { useTranslation } from "@/lib/i18n";
import { makeTr, fill, CITY_BG } from "@/lib/tools-i18n";
import { PRICER_BG } from "@/lib/tools-i18n/pricer";
import {
  DWDM_PRICES,
  DWDM_ROUTE_OVERRIDES,
  PRICING_POP_NODES,
  type PopRegion,
} from "@/lib/pricing";

/* ─── Pricing ───
 *
 * This widget shows DWDM "From" pricing as a lightweight preview so
 * users see a real number before they scroll to the configurator.
 *
 * DELIBERATE SIMPLIFICATIONS vs UnifiedConfigurator:
 *  - Only DWDM (no L2VPN / IP Transit / Dark Fiber). The hero is a
 *    single-format preview; the full configurator handles product mix.
 *  - No commitment / burst / redundancy modifiers. The hero quotes an
 *    unmodified "From" base — the configurator layers the discounts.
 *  - Route-region resolution is symmetric (max region on either end)
 *    while the configurator uses origin's region. This is intentional:
 *    the hero would otherwise show two different "From" prices for the
 *    same city pair depending on the direction picked.
 *
 * All base figures come from src/lib/pricing.ts. If the DWDM matrix or
 * route overrides change there, this widget follows automatically.
 */

type CapacityKey = "1G" | "10G" | "100G" | "400G";
const CAPACITY_STEPS: CapacityKey[] = ["1G", "10G", "100G", "400G"];

/* Slider label → numeric bandwidth used by pricing.ts DWDM tables. */
const CAPACITY_TO_BW: Record<CapacityKey, number> = {
  "1G": 1,
  "10G": 10,
  "100G": 100,
  "400G": 400,
};

/* Region ranking for the symmetric "worst-of" pick between origin and
 * destination. Higher wins. Any PoP unknown to pricing.ts (should not
 * happen — hero uses INTERNATIONAL_POPS which are all in the pricing
 * table) falls back to europe (rank 0). */
const REGION_RANK: Record<PopRegion, number> = {
  "bulgaria-domestic": 0,
  europe: 0,
  caucasus: 1,
  "middle-east": 2,
};

function regionOf(popId: string): PopRegion {
  return PRICING_POP_NODES.find((n) => n.id === popId)?.region ?? "europe";
}

function routeRegion(origin: string, destination: string): PopRegion {
  const ro = regionOf(origin);
  const rd = regionOf(destination);
  return REGION_RANK[ro] >= REGION_RANK[rd] ? ro : rd;
}

function calcPrice(origin: string, destination: string, capacity: CapacityKey): number {
  const bw = CAPACITY_TO_BW[capacity];
  const key = [origin, destination].sort().join("|");
  const override = DWDM_ROUTE_OVERRIDES[key]?.[bw];
  if (override !== undefined) return override;
  const region = routeRegion(origin, destination);
  return DWDM_PRICES[region]?.[bw] ?? 0;
}

/* ─── POP options — use international POPs from network-data ─── */
const POP_OPTIONS = INTERNATIONAL_POPS.map((p) => ({
  id: p.id,
  label: `${p.flag} ${p.city}`,
  flag: p.flag,
  city: p.city,
}));

/* ─── Component ─── */
export function HeroMiniPricer() {
  const { language } = useTranslation();
  const tr = makeTr(language, PRICER_BG);
  const city = makeTr(language, CITY_BG);
  const [origin, setOrigin] = useState(POP_OPTIONS[0].id);
  const [destination, setDestination] = useState(
    POP_OPTIONS.find((p) => p.id === "frankfurt")?.id ?? POP_OPTIONS[1].id
  );
  // Default to 1G (index 0). Previously defaulted to 100G, which quoted
  // "Est. from €1,500/mo" while the main configurator's opening price is
  // "Starting From €120/mo" (1G). Users saw both figures within one scroll
  // and read them as contradictory. Aligning defaults means both surfaces
  // now agree on the entry-price anchor — the slider is still there for
  // anyone who wants to see 10G/100G/400G pricing without leaving the hero.
  const [capacityIndex, setCapacityIndex] = useState(0);

  const capacity = CAPACITY_STEPS[capacityIndex];
  const price = calcPrice(origin, destination, capacity);
  const sameRoute = origin === destination;

  /* Debounced interaction tracking — once per field per 2s */
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const trackInteraction = useCallback(
    (field: string, value: string) => {
      if (debounceTimers.current[field]) {
        clearTimeout(debounceTimers.current[field]);
      }
      debounceTimers.current[field] = setTimeout(() => {
        trackEvent("mini_pricer_interact", { field, value } as Parameters<typeof trackEvent>[1]);
      }, 2000);
    },
    []
  );

  /* Cleanup timers on unmount */
  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  function handleOriginChange(val: string) {
    setOrigin(val);
    trackInteraction("origin", val);
  }

  function handleDestinationChange(val: string) {
    setDestination(val);
    trackInteraction("destination", val);
  }

  function handleCapacityChange(idx: number) {
    setCapacityIndex(idx);
    trackInteraction("capacity", CAPACITY_STEPS[idx]);
  }

  function handleSubmit() {
    const params = new URLSearchParams({
      origin,
      destination,
      capacity,
    });
    trackEvent("mini_pricer_submit", { origin, destination, capacity } as Parameters<typeof trackEvent>[1]);
    window.location.href = localizeHref(`/#configurator?${params.toString()}`, language);
  }

  return (
    <div
      className="mt-6 w-full max-w-3xl mx-auto rounded-2xl p-4 sm:p-5"
      style={{
        background: "var(--hover-bg)",
        border: "1px solid var(--card-border)",
        backdropFilter: "blur(20px)",
      }}
    >
      {/* Label */}
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">
        {tr("Instant indicative pricing")}
      </p>

      <div className="flex flex-col lg:flex-row items-stretch lg:items-end gap-3">
        {/* From / To selects */}
        {/* lg:min-w keeps "🇩🇪 Frankfurt" whole: the pair was squeezed to
            97px per select by the slider and the price/CTA block. */}
        <div className="flex flex-1 gap-2 lg:min-w-[300px]">
          <div className="flex-1 min-w-0">
            <label className="block text-[10px] font-medium text-muted-foreground mb-1" htmlFor="mini-pricer-from">
              {tr("From")}
            </label>
            <select
              id="mini-pricer-from"
              value={origin}
              onChange={(e) => handleOriginChange(e.target.value)}
              className="w-full rounded-xl px-3 py-2 text-xs font-medium text-foreground focus:outline-none focus:ring-1 focus:ring-[var(--accent)] transition-all"
              style={{
                background: "var(--card-border)",
                border: "1px solid var(--card-border)",
              }}
            >
              {POP_OPTIONS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.flag} {city(p.city)}
                </option>
              ))}
            </select>
          </div>

          {/* Arrow */}
          <div className="flex items-end pb-2">
            <svg
              className="w-4 h-4 text-muted-foreground shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>

          <div className="flex-1 min-w-0">
            <label className="block text-[10px] font-medium text-muted-foreground mb-1" htmlFor="mini-pricer-to">
              {tr("To")}
            </label>
            <select
              id="mini-pricer-to"
              value={destination}
              onChange={(e) => handleDestinationChange(e.target.value)}
              className="w-full rounded-xl px-3 py-2 text-xs font-medium text-foreground focus:outline-none focus:ring-1 focus:ring-[var(--accent)] transition-all"
              style={{
                background: "var(--card-border)",
                border: "1px solid var(--card-border)",
              }}
            >
              {POP_OPTIONS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.flag} {city(p.city)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Capacity slider */}
        <div className="flex-1 min-w-0 lg:max-w-[200px]">
          <div className="flex justify-between items-center mb-1">
            <label className="text-[10px] font-medium text-muted-foreground" htmlFor="mini-pricer-capacity">
              {tr("Capacity")}
            </label>
            <span
              className="text-[10px] font-bold font-mono text-eyebrow"
            >
              {capacity}
            </span>
          </div>
          <input
            id="mini-pricer-capacity"
            type="range"
            min={0}
            max={3}
            step={1}
            value={capacityIndex}
            onChange={(e) => handleCapacityChange(Number(e.target.value))}
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, var(--accent) 0%, var(--accent) ${(capacityIndex / 3) * 100}%, var(--card-border) ${(capacityIndex / 3) * 100}%, var(--card-border) 100%)`,
              accentColor: "var(--accent)",
            }}
            aria-label={fill(tr("Capacity: {capacity}"), { capacity })}
          />
          <div className="flex justify-between mt-1">
            {CAPACITY_STEPS.map((s) => (
              <span key={s} className="text-[9px] text-muted-foreground font-mono">
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* Price + CTA */}
        <div className="flex items-end gap-2">
          <div className="text-right min-w-[80px]">
            <div className="text-[10px] text-muted-foreground mb-0.5">{tr("Est. from")}</div>
            {sameRoute ? (
              <div className="text-sm font-bold text-muted-foreground">—</div>
            ) : (
              <div
                className="text-lg font-extrabold font-mono"
                style={{
                  background: "linear-gradient(135deg, #0066ff 0%, #00d4aa 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                €{price.toLocaleString()}
                <span className="text-[10px] font-normal ml-0.5">{tr("/mo")}</span>
              </div>
            )}
          </div>

          <button
            onClick={handleSubmit}
            disabled={sameRoute}
            className="shrink-0 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: sameRoute ? "var(--card-border)" : "var(--accent)",
              color: sameRoute
                ? "var(--muted-foreground)"
                : "var(--on-accent)",
              boxShadow: sameRoute ? "none" : "0 0 20px rgba(0,212,170,0.2)",
            }}
            aria-label={tr("Price my connection — open configurator")}
          >
            {tr("Price my connection")}
          </button>
        </div>
      </div>
    </div>
  );
}
